import type { Concept } from '@/types/concept';

/**
 * Most recent clean draft concept to resume, or null if none exists.
 *
 * "Clean draft" = no main render yet AND still in draft state. A concept with
 * is_draft=false but no main render means rendering is in progress — skip it.
 *
 * Gates on `has_main_render`, NOT a render count: the concept-list resource
 * (ConceptResource) ships `has_main_render` + `renders_count` (note the "s"),
 * never `render_count`. Mirrors Apple's determineAddConceptAction
 * (`!hasMainRender && isDraft`) and Android's determineAddConceptHandler. The
 * previous `render_count === 0` test read an absent field (always undefined),
 * so it never matched and the CTA always opened the angle-picker sheet instead
 * of resuming the draft.
 *
 * Used by the "Create concept" entry points (Brief CTA + angles library):
 * reuse an existing empty draft instead of piling up new ones.
 */
export function findResumableDraftConcept(concepts: Concept[]): Concept | null {
  const sorted = [...concepts].sort(
    (a, b) =>
      (b.attributes.project_sequence_number ?? 0) -
      (a.attributes.project_sequence_number ?? 0)
  );

  return (
    sorted.find(
      (c) =>
        c.attributes.has_main_render === false && c.attributes.is_draft === true
    ) ?? null
  );
}
